import type { Router } from 'vue-router';
import Core from "@/core/Core";

/**
 * DialogsRouter - управление навигацией и состоянием диалогов через URL
 *
 * Функционал:
 * - Сохраняет открытые карточки в query: ?dialog=tender:123&dialog=partner:456
 * - Табы реестров хранятся в hash: #tenders-tab=active
 * - Восстанавливает состояние при загрузке страницы из URL
 * - Поддерживает историю браузера (кнопка "Назад" закрывает карточки)
 * - Позволяет шарить ссылки с конкретными открытыми карточками
 *
 * Имя сущности — строка, заданная при создании Dialog и приведённая к camelCase
 * (routeEntityName). Никакого центрального перечня типов нет: реестром
 * сущностей служит сам DialogsRegistry, карта строится из routeEntityName
 * зарегистрированных диалогов.
 *
 * Использование:
 * - DialogsRouter.init(router) - инициализация при старте приложения
 * - DialogsRouter.push('tender', 123) - добавить карточку в URL
 * - DialogsRouter.remove('tender', 123) - убрать карточку из URL
 *
 * Диалоги для восстановления из URL ищутся в DialogsRegistry по routeEntityName.
 *
 * Пример URL: /tenders?dialog=tender:123&dialog=partner:456#tenders-tab=active
 */
class DialogsRouter {
    private static router: Router;

    /**
     * Очередь навигаций.
     *
     * router.push асинхронный, а currentRoute обновляется только после завершения
     * навигации. Несколько push/remove подряд (восстановление стека карточек из URL,
     * открытие двух карточек кодом друг за другом) читали бы устаревший стек и
     * затирали друг друга, а vue-router вдобавок отменяет pending-навигацию при
     * старте новой. Поэтому каждая мутация URL встаёт в цепочку и вычисляет query
     * только после завершения предыдущей навигации.
     */
    private static navigationQueue: Promise<unknown> = Promise.resolve();

    /**
     * Поставить навигацию в очередь.
     * Колбэк вызывается после завершения предыдущей навигации, поэтому
     * query внутри него вычисляется от актуального currentRoute.
     * Отказы навигации (отмена, дубликат) глотаются, чтобы не рвать цепочку.
     */
    private static enqueueNavigation(navigate: () => Promise<unknown>): Promise<unknown> {
        this.navigationQueue = this.navigationQueue
            .then(() => navigate())
            .catch(() => {});

        return this.navigationQueue;
    }

    /**
     * Получить текущий route
     * Избавляет от необходимости писать .value каждый раз
     */
    private static get currentRoute() {
        return this.router.currentRoute.value;
    }

    /**
     * Инициализация сервиса с router
     * Подписывается на готовность приложения и восстанавливает диалоги из URL
     *
     * @param router - экземпляр Vue Router
     */
    static async init(router: Router): Promise<void> {
        this.router = router;

        // Подписываемся на событие готовности приложения
        Core.eventBus.AppReady.addEventListener( async () => {
            // Ждём полной инициализации роутера
            await this.router.isReady();

            // Восстанавливаем диалоги из URL
            this.restoreDialogsFromUrl();
        });
    }

    /**
     * Добавляет карточку в стек навигации и обновляет URL
     * @param entityName - имя сущности (routeEntityName диалога)
     * @param id - идентификатор сущности
     *
     * Пример: push('partner', 123) → URL: ?dialog=partner:123
     * Создаёт новую запись в истории браузера
     */
    static push(entityName: string, id: number): void {
        this.enqueueNavigation(() => this.router.push({
            query: {
                ...this.currentRoute.query,
                dialog: this.getStack().concat(`${entityName}:${id}`)
            },
            hash: this.currentRoute.hash
        }));
    }

    /**
     * Убирает карточку из стека навигации и обновляет URL
     * @param entityName - имя сущности (routeEntityName диалога)
     * @param id - идентификатор сущности
     *
     * Пример: remove('partner', 123) → убирает partner:123 из URL
     * Создаёт новую запись в истории браузера: кнопка "Назад" после
     * закрытия карточки переоткрывает её (симметрично закрытию по "Назад")
     */
    static remove(entityName: string, id: number): void {
        this.enqueueNavigation(() => {
            const item = `${entityName}:${id}`;

            const filtered = this.getStack().filter(s => s !== item);

            return this.router.push({
                query: {
                    ...this.currentRoute.query,
                    dialog: filtered.length > 0 ? filtered : undefined  // Если массив пустой - удаляем параметр
                },
                hash: this.currentRoute.hash
            });
        });
    }

    /**
     * Читает стек карточек из URL
     * @returns массив строк в формате ["partner:123", "tender:456"]
     *
     * Vue Router автоматически парсит ?dialog=partner:123&dialog=tender:456
     * в массив или единичное значение
     */
    static getStack(): string[] {
        const dialog = this.currentRoute.query?.dialog;

        if (!dialog) return [];
        return Array.isArray(dialog) ? dialog : [dialog];
    }

    /**
     * Восстанавливает открытые диалоги из URL при загрузке страницы
     *
     * Читает стек из URL, очищает его, и открывает карточки через DialogsRegistry.
     * Каждый диалог сам добавит карточку обратно в URL через push() в своём show().
     * Вызывается один раз при инициализации приложения
     */
    private static async restoreDialogsFromUrl(): Promise<void> {
        // Получаем все карточки из URL
        const items = this.getStack();

        // Если карточек нет - выходим
        if (items.length === 0) {
            return;
        }

        // Ждём очистки query
        await this.clearDialogsFromQuery();

        // Ленивый импорт реестра, чтобы не образовывать статический цикл
        // DialogsRouter → DialogsRegistry → Dialog → DialogsRouter
        const { default: DialogsRegistry } = await import('@/core/services/Dialogs/DialogsRegistry');

        // Открываем каждую карточку через зарегистрированный в реестре диалог
        for (const item of items) {
            // Парсим строку "partner:123" → entityName: "partner", id: 123
            const [entityName, rawId] = item.split(':');
            const id = parseInt(rawId);

            if (Number.isNaN(id)) {
                Core.warn(`Некорректный id в ссылке на карточку: ${item}`);
            } else {
                // Находим диалог-карточку по имени сущности
                const dialog = DialogsRegistry.getDialogByEntityName(entityName);

                if (dialog) {
                    // show() сам вызовет push() и добавит карточку обратно в URL
                    dialog.show({ id });
                } else {
                    Core.warn(`Не найден диалог для типа сущности: ${entityName}`);
                }
            }
        }
    }


    /**
     * Очищает параметр dialog из query
     * Используется перед восстановлением диалогов
     */
    private static async clearDialogsFromQuery(): Promise<void> {
        await this.enqueueNavigation(() => {
            const { dialog, ...restQuery } = this.currentRoute.query;

            return this.router.replace({
                query: restQuery,
                hash: this.currentRoute.hash
            });
        });
    }
}

export default DialogsRouter;